import React, {useEffect} from 'react';
import {getAvailableTariffs, getTariffOfferComparator} from "@/utils";
import {
    AddressType,
    EcommerceProductValue,
    HouseholdConfigurationType,
    ReduxStateType, TariffPositionsType, TrackingInfoType,
} from "@/types/av-check";
import {buildSuccessData, buildSuccessDataUpdate, pushToTefDataLayer} from "@/services/tracking";
import {useDispatch, useSelector} from "react-redux";
import { actions } from '@/redux/av-check';

const DEFAULT_TRACKING_INFO_OBJ = {
    isFirstSliderView : false,
    sliderTariffPositions : {}
}

const DEFAULT_HOUSE_HOLD_CONFIG_OBJ = {
    personInHousehold: 0,
    usageTypes: []
}

type SliderProps = {
    config?: string,
    isCmsCall: boolean
};

const Slider: React.FC<SliderProps> = React.forwardRef(({config, children, isCmsCall}) => {
    const tariffCatalogItems: any = React.Children.toArray(children);

    const ecommerceProductValues:EcommerceProductValue[] = useSelector((state: ReduxStateType) => state.result?.ecommerceProductValues) || [];
    const address:AddressType = useSelector((state: ReduxStateType) => state.result!.address);
    const trackingInfo:TrackingInfoType = useSelector((state: ReduxStateType) => state?.trackingInfo) || DEFAULT_TRACKING_INFO_OBJ;
    const householdConfiguration:HouseholdConfigurationType = useSelector((state: ReduxStateType) => state.result?.householdConfiguration) || DEFAULT_HOUSE_HOLD_CONFIG_OBJ;
    const dispatch = useDispatch();

    let numOfClicks: number = 0;
    let highlightedIndex: number = 0;
    let trackedIndices: number[] =[];

    const getTariffsInCatalog = (tariffCatalogItem:any) => getAvailableTariffs(tariffCatalogItem.props.tariffCatalog).sort(getTariffOfferComparator());

    function scrollHighlightedTariffIntoView(nextButton: HTMLButtonElement) {
        if(numOfClicks > 0 ){
            numOfClicks--;
            setTimeout(function (){
                nextButton.click();
            }, 5);
        }
    }

    const waitForHighlightedTariffInView = async () => {
        while (!highlightedCatalogItemInView()) {
            await new Promise((resolve) => requestAnimationFrame(resolve));
        }
    }

    const highlightedCatalogItemInView = () => {
        const sliderColumns =  document.querySelectorAll('.item.slick-slide.slick-active');
        let found:boolean=false;
        sliderColumns.forEach((column) => {
            const colIndexValue = column.getAttribute("data-slick-index");
            const colIndex = parseInt(colIndexValue!);
            if (tariffCatalogItems[colIndex].props.highlightLabel !== undefined)
                setTimeout(() => { }, 500);
                found = true;
        });
       return found;
    }

    const pushSuccessTracking = (sliderTariffPositions:TariffPositionsType) => {
        const sliderColumns =  (document.querySelectorAll('.item.slick-slide.slick-active'));
        const activeEcommerceProductValues:EcommerceProductValue[]=[];
        sliderColumns.forEach((column) => {
            const colIndex = parseInt(column.getAttribute("data-slick-index")!);
            trackedIndices.push(colIndex);
            getTariffsInCatalog(tariffCatalogItems[colIndex]).forEach((tariff) => {
                const ecommerceProductValue = ecommerceProductValues.find(ecommerceObject => ecommerceObject.name==tariff.offerName)!;
                activeEcommerceProductValues.push(ecommerceProductValue);
            });
        });
        if(activeEcommerceProductValues.length > 6){
            pushToTefDataLayer(window, buildSuccessData(address,householdConfiguration, activeEcommerceProductValues.slice(0,6),sliderTariffPositions, isCmsCall));
            activeEcommerceProductValues.splice(0,6);
            activeEcommerceProductValues.length && pushToTefDataLayer(window, buildSuccessDataUpdate(activeEcommerceProductValues,sliderTariffPositions));
        }else{
            pushToTefDataLayer(window, buildSuccessData(address,householdConfiguration, activeEcommerceProductValues,sliderTariffPositions, isCmsCall));
        }
    }

    const pushUpdateTracking = (sliderTariffPositions:TariffPositionsType) => {
        const sliderColumns =  (document.querySelectorAll('.item.slick-slide.slick-active'));
        const activeEcommerceProductValues:EcommerceProductValue[]=[];
        sliderColumns.forEach((column) => {
            const colIndex = parseInt(column.getAttribute("data-slick-index")!);
            if (!trackedIndices.includes(colIndex)) {
                trackedIndices.push(colIndex);
                getTariffsInCatalog(tariffCatalogItems[colIndex]).forEach((tariff) => {
                    const ecommerceProductValue = ecommerceProductValues.find(ecommerceObject => ecommerceObject.name==tariff.offerName)!;
                    activeEcommerceProductValues.push(ecommerceProductValue);
                });
            }
        });
        activeEcommerceProductValues.length && pushToTefDataLayer(window, buildSuccessDataUpdate(activeEcommerceProductValues,sliderTariffPositions));
    }

    useEffect(()=>{
        waitForHighlightedTariffInView().then(() => {
            let tariffIndex=0;
            const sliderTariffPositions : TariffPositionsType= {};
            tariffCatalogItems.forEach((tariffCatalogItem:any) => {
                getTariffsInCatalog(tariffCatalogItem).forEach((tariff:any)=>{
                     sliderTariffPositions[tariff.offerName]= tariffIndex+=1;
                });
            })

            trackingInfo.isFirstSliderView ? pushSuccessTracking(sliderTariffPositions) : pushUpdateTracking(sliderTariffPositions);

            dispatch(actions.setTrackingInfo({isFirstSliderView: false, sliderTariffPositions: sliderTariffPositions}));

            //Get scrolling buttons and dots
            const nextButton: HTMLButtonElement | null = document.querySelector('.slick-next');
            const prevButton: HTMLButtonElement | null = document.querySelector('.slick-prev');
            const dots: HTMLButtonElement | null = document.querySelector('.slick-dots');

            nextButton?.addEventListener('click', () => pushUpdateTracking(sliderTariffPositions));
            prevButton?.addEventListener('click', () => pushUpdateTracking(sliderTariffPositions));
            dots?.addEventListener('click', () => pushUpdateTracking(sliderTariffPositions));
        })

    },[]);

    useEffect(() => {

        let observer = new MutationObserver(() => {
            const sliderColumns =  (document.querySelectorAll('.item.slick-slide.slick-active'));
            const slider = document.querySelector('.slick-track');
            const nextButton: HTMLButtonElement | null = document.querySelector('.slick-next:not(.slick-disabled)');
            if (slider && nextButton && sliderColumns) {
                removeObserver(observer);
                const numOfSliderColumns = sliderColumns.length;
                slider.addEventListener('transitioncancel', () => scrollHighlightedTariffIntoView(nextButton));
                slider.addEventListener('transitionend', () => scrollHighlightedTariffIntoView(nextButton));

                tariffCatalogItems.forEach((child: any, index: number) => {
                    if (child.props.highlightLabel !== undefined) {
                        highlightedIndex = index;
                    }

                });

                numOfClicks = (highlightedIndex + 1) - numOfSliderColumns;
                if (numOfClicks > 0) {
                    numOfClicks--;
                    nextButton.click();
                }

            }
        })
        observer.observe(document, {subtree: true, childList: true});
    })

    const removeObserver = (observer: MutationObserver) => {
        observer.disconnect();
    }

    return (
        <section className="tile-slider">
            <div className="slider" data-slider-config={config}>
                {React.Children.map(children, (child) => (
                    <div className="item">
                        {child}
                    </div>
                ))}
            </div>
        </section>
    );
});

export default Slider;
